export interface CenterstageScreenshots {
  medium?: string;
  regular?: string;
  large?: string;
}

type CenterstageScreenshotSize = 'large' | 'regular' | 'medium';

const CENTERSTAGE_SCREENSHOT_PATH_PATTERN = /^\/\d+\/(large|medium|regular)\/[^/]+$/i;

function normalizeUrl(url?: string): string | undefined {
  const trimmed = url?.trim();
  return trimmed ? trimmed : undefined;
}

function isLikelyCenterstageScreenshotUrl(url: URL): boolean {
  const host = url.hostname.toLowerCase();
  const isCenterstageHost =
    host.includes('centerstage') ||
    host.includes('emtrain') ||
    host.endsWith('cloudfront.net');

  return isCenterstageHost && CENTERSTAGE_SCREENSHOT_PATH_PATTERN.test(url.pathname);
}

function buildCenterstageScreenshotUrl(
  url: URL,
  size: CenterstageScreenshotSize
): string {
  const nextUrl = new URL(url.toString());
  nextUrl.pathname = nextUrl.pathname.replace(
    /\/(large|medium|regular)\//i,
    `/${size}/`
  );
  return nextUrl.toString();
}

function dedupeUrls(urls: Array<string | undefined>): string[] {
  return urls.filter((url, index, list): url is string => Boolean(url) && list.indexOf(url) === index);
}

export function getCenterstageScreenshotFallbackUrls(url?: string): string[] {
  const normalizedUrl = normalizeUrl(url);
  if (!normalizedUrl) {
    return [];
  }

  try {
    const parsedUrl = new URL(normalizedUrl);

    if (!isLikelyCenterstageScreenshotUrl(parsedUrl)) {
      return [normalizedUrl];
    }

    if (parsedUrl.pathname.includes('/large/')) {
      return dedupeUrls([
        normalizedUrl,
        buildCenterstageScreenshotUrl(parsedUrl, 'regular'),
        buildCenterstageScreenshotUrl(parsedUrl, 'medium'),
      ]);
    }

    if (parsedUrl.pathname.includes('/regular/')) {
      return dedupeUrls([
        normalizedUrl,
        buildCenterstageScreenshotUrl(parsedUrl, 'medium'),
      ]);
    }

    return [normalizedUrl];
  } catch {
    return [normalizedUrl];
  }
}

export function getCenterstageThumbnailUrl(
  screenshots?: CenterstageScreenshots
): string | undefined {
  return normalizeUrl(screenshots?.medium) ??
    normalizeUrl(screenshots?.regular) ??
    normalizeUrl(screenshots?.large);
}

export function getCenterstagePosterUrl(
  screenshots?: CenterstageScreenshots
): string | undefined {
  return normalizeUrl(screenshots?.large) ??
    normalizeUrl(screenshots?.regular) ??
    normalizeUrl(screenshots?.medium);
}
